import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Button, Modal, Tag } from 'antd';
import { ExportOutlined, HistoryOutlined, CarOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import TripHistoryModal from './TripHistoryModal';
import { getTripStatusHistoryV2 } from 'store/tripSliceV2';

const { Title, Text } = Typography;

const ModalIconWrapper = styled.div`
	background-color: #e6f7ff;
	border-radius: 8px;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 12px;
`;

const InfoLabel = styled(Text)`
	font-size: 11px;
	color: #8c8c8c;
	display: block;
	margin-bottom: 4px;
	letter-spacing: 0.3px;
`;
const Required = styled.span`
	color: red;
`;

const InfoValue = styled(Text)`
	font-size: 13px;
	font-weight: 600;
	color: #1a1a2e;
	display: block;
	min-height: 20px;
`;

const GridItem = styled.div`
	padding: 10px 14px;
	background: #fff;
	height: 100%;
	box-sizing: border-box;
`;

const RowWrapper = styled.div`
	margin-bottom: 10px;
`;
const capitalizeFirst = (text) => {
	if (!text) return '';
	return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

function TripDetailsCard({ tripData, isValid }) {
	const dispatch = useDispatch();
	const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
	const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
	const [historyData, setHistoryData] = useState([]);

	useEffect(() => {
		if (isHistoryModalOpen) {
			const tripId = tripData?.tripInfo?.id || tripData?.tripInfo?.trip_id;
			if (tripId) {
				dispatch(
					getTripStatusHistoryV2({
						trip_id: tripId,
					})
				)
					.then((res) => {
						const data = res?.data?.timeline || [];
						const mappedHistory = data.map((item) => {
							const rawTime =
								item.timestamp || item.created_at || item.timestamp_display;
							const formattedTime = rawTime
								? moment(rawTime)
										.add(5, 'hours')
										.add(30, 'minutes')
										.format('DD/MM/YYYY hh:mm A')
								: '-';
							return {
								...item,
								timestamp_display: formattedTime,
							};
						});
						setHistoryData(mappedHistory);
					})
					.catch((err) => {
						console.error('Failed to fetch history:', err);
					});
			}
		}
	}, [isHistoryModalOpen, tripData, dispatch]);

	const isInvalidTrip =
		isValid === false ||
		tripData?.tripInfo?.is_valid === false ||
		tripData?.tripInfo?.is_valid_trip === false;

	const formatTripStatus = (status) => {
		if (typeof status !== 'string') return status || '';
		return status
			.split('_')
			.map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''))
			.join(' ');
	};

	const formatDateTime = (dateString) => {
		if (!dateString) return '';
		return moment(dateString).add(5, 'hours').add(30, 'minutes').format('DD/MM/YYYY hh:mm A');
	};

	return (
		<Card
			bodyStyle={{ padding: '0px' }}
			style={{ borderRadius: '8px', marginBottom: '16px', border: '1px solid #f0f0f0' }}
		>
			<div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
				<Title level={5} style={{ margin: 0 }}>
					Trip Details
				</Title>
			</div>

			<div style={{ padding: '24px' }}>
				<Row justify="space-between" style={{ marginBottom: '16px' }}>
					<Col>
						<Text type="secondary" style={{ fontSize: '13px' }}>
							Current Status
						</Text>
					</Col>
					<Col>
						<Text strong style={{ color: '#1890ff' }}>
							{formatTripStatus(tripData?.tripInfo?.trip_status)}
						</Text>
						{isInvalidTrip && (
							<Text
								strong
								style={{
									marginLeft: '6px',
									padding: '2px 8px',
									borderRadius: '4px',
									backgroundColor: '#fff1f0',
									color: '#ff4d4f',
									fontSize: '12px',
								}}
							>
								Invalid Trip
							</Text>
						)}
					</Col>
				</Row>

				<Row justify="space-between" style={{ marginBottom: '16px' }}>
					<Col>
						<Text type="secondary" style={{ fontSize: '13px' }}>
							Client
						</Text>
					</Col>
					<Col>
						<Text strong style={{ fontSize: '13px' }}>
							{tripData?.tripInfo?.client_name}
						</Text>
					</Col>
				</Row>

				<Row justify="space-between" style={{ marginBottom: '16px' }}>
					<Col>
						<Text type="secondary" style={{ fontSize: '13px' }}>
							Project
						</Text>
					</Col>
					<Col>
						<Text strong style={{ fontSize: '13px' }}>
							{tripData?.tripInfo?.project_name}
						</Text>
					</Col>
				</Row>

				<Row justify="space-between" style={{ marginBottom: '24px' }}>
					<Col>
						<Text type="secondary" style={{ fontSize: '13px' }}>
							Vendor
						</Text>
					</Col>
					<Col>
						<Text strong style={{ fontSize: '13px' }}>
							{tripData?.tripInfo?.vendor_name}
						</Text>
					</Col>
				</Row>

				<Button
					type="primary"
					block
					icon={<ExportOutlined />}
					style={{
						backgroundColor: '#1f3e5a',
						borderColor: '#1f3e5a',
						borderRadius: '6px',
					}}
					onClick={() => setIsDetailModalOpen(true)}
				>
					View More Details
				</Button>
			</div>

			<Modal
				visible={isDetailModalOpen}
				onCancel={() => setIsDetailModalOpen(false)}
				width={800}
				footer={
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							gap: '16px',
							padding: '12px 0',
						}}
					>
						<Button
							icon={<HistoryOutlined />}
							onClick={() => setIsHistoryModalOpen(true)}
							style={{ borderRadius: '6px' }}
						>
							View Trip History
						</Button>
						<Button
							type="primary"
							style={{
								backgroundColor: '#1f3e5a',
								borderRadius: '6px',
								width: '120px',
							}}
							onClick={() => setIsDetailModalOpen(false)}
						>
							Close
						</Button>
					</div>
				}
				title={
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<ModalIconWrapper>
							<CarOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
						</ModalIconWrapper>
						<Title level={4} style={{ margin: 0, color: '#1f3e5a' }}>
							Complete Trip Information
						</Title>
					</div>
				}
			>
				<div style={{ padding: '16px 0 0 0' }}>
					<RowWrapper>
						<Row gutter={[10, 0]} align="stretch" style={{ alignItems: 'stretch' }}>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>Client</InfoLabel>
									<InfoValue>{tripData?.tripInfo?.client_name}</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>Project</InfoLabel>
									<InfoValue>{tripData?.tripInfo?.project_name}</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>Vendor</InfoLabel>
									<InfoValue>{tripData?.tripInfo?.vendor_name}</InfoValue>
								</GridItem>
							</Col>
						</Row>
					</RowWrapper>

					<RowWrapper>
						<Row gutter={[10, 0]} style={{ alignItems: 'stretch' }}>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>Reporting Date &amp; Time</InfoLabel>
									<InfoValue>
										{formatDateTime(tripData?.tripInfo?.reporting_date_time)}
									</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>Start Date &amp; Time</InfoLabel>
									<InfoValue>
										{formatDateTime(
											tripData?.tripInfo?.actual_start_time ||
												tripData?.tripInfo?.start_date_time ||
												tripData?.tripInfo?.reporting_date_time
										)}
									</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>End Date &amp; Time</InfoLabel>
									<InfoValue>
										{formatDateTime(
											tripData?.tripInfo?.actual_end_time ||
												tripData?.tripInfo?.end_date_time
										)}
										{tripData?.timings?.total_duration_hrs && (
											<Text
												type="secondary"
												style={{
													fontSize: '12px',
													fontWeight: 'normal',
													marginLeft: '6px',
												}}
											>
												{tripData?.timings?.total_duration_hrs}
											</Text>
										)}
									</InfoValue>
								</GridItem>
							</Col>
						</Row>
					</RowWrapper>

					<RowWrapper>
						<Row gutter={[10, 0]} style={{ alignItems: 'stretch' }}>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Source Location<Required>*</Required>
									</InfoLabel>
									<InfoValue>
										{tripData?.sourceLocation ||
											tripData?.tripInfo?.source_location ||
											'-'}
									</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Destination Location<Required>*</Required>
									</InfoLabel>
									<InfoValue>
										{tripData?.destinationLocation ||
											tripData?.tripInfo?.destination_location ||
											'-'}
									</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>Trip Type</InfoLabel>
									<InfoValue>
										{capitalizeFirst(tripData?.tripInfo?.trip_type)}
									</InfoValue>
								</GridItem>
							</Col>
						</Row>
					</RowWrapper>

					<RowWrapper>
						<Row gutter={[10, 0]} style={{ alignItems: 'stretch' }}>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Vendor Type<Required>*</Required>
									</InfoLabel>
									<InfoValue>
										{capitalizeFirst(tripData?.tripInfo?.vendor_type)}
									</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Vendor Name<Required>*</Required>
									</InfoLabel>
									<InfoValue>{tripData?.tripInfo?.vendor_name}</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>City</InfoLabel>
									<InfoValue>{tripData?.tripInfo?.city}</InfoValue>
								</GridItem>
							</Col>
						</Row>
					</RowWrapper>

					<RowWrapper>
						<Row gutter={[10, 0]} style={{ alignItems: 'stretch' }}>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Project Name<Required>*</Required>
									</InfoLabel>
									<InfoValue>{tripData?.tripInfo?.project_name}</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Client Rate<Required>*</Required>
									</InfoLabel>
									<InfoValue>{tripData?.clientRate}</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Vendor Rate<Required>*</Required>
									</InfoLabel>
									<InfoValue>{tripData?.vendorRate}</InfoValue>
								</GridItem>
							</Col>
						</Row>
					</RowWrapper>

					<RowWrapper>
						<Row gutter={[10, 0]} style={{ alignItems: 'stretch' }}>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Driver Number<Required>*</Required>
									</InfoLabel>
									<InfoValue>{tripData?.driver?.driver_number}</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Driver Name<Required>*</Required>
									</InfoLabel>
									<InfoValue>{tripData?.driver?.driver_name}</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Vehicle Type<Required>*</Required>
									</InfoLabel>
									<InfoValue>{tripData?.vehicle?.vehicle_type}</InfoValue>
								</GridItem>
							</Col>
						</Row>
					</RowWrapper>

					<RowWrapper>
						<Row gutter={[10, 0]} style={{ alignItems: 'stretch' }}>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Vehicle Number<Required>*</Required>
									</InfoLabel>
									<InfoValue>
										{tripData?.vehicle?.vehicle_number}
										{tripData?.isCrossVendor && (
											<span
												style={{
													marginLeft: 8,
													padding: '2px 8px',
													borderRadius: 4,
													fontSize: 12,
													fontWeight: 500,
													backgroundColor: '#FFE7D899',
													color: '#E46802',
												}}
											>
												Belongs to : {tripData?.vehicleVendorName}
											</span>
										)}
									</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>
										Route Name<Required>*</Required>
									</InfoLabel>
									<InfoValue>{tripData?.tripInfo?.route_name}</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>Adhoc Request ID</InfoLabel>
									<InfoValue>{tripData?.tripInfo?.adhoc_request_id}</InfoValue>
								</GridItem>
							</Col>
						</Row>
					</RowWrapper>

					<RowWrapper>
						<Row gutter={[10, 0]} style={{ alignItems: 'stretch' }}>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>Advance Amount</InfoLabel>
									<InfoValue>
										{tripData?.advancePaymentDetail
											? `₹ ${tripData.advancePaymentDetail}`
											: 'N/A'}
									</InfoValue>
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>Advance Status</InfoLabel>
									{tripData?.advacnePaymentStatus ? (
										<Tag
											color="success"
											style={{ fontWeight: 600, marginTop: '2px' }}
										>
											{tripData?.advacnePaymentStatus}
										</Tag>
									) : (
										<InfoValue>N/A</InfoValue>
									)}
								</GridItem>
							</Col>
							<Col span={8} style={{ display: 'flex' }}>
								<GridItem>
									<InfoLabel>Trip Status</InfoLabel>
									<InfoValue style={{ color: '#1890ff' }}>
										{formatTripStatus(tripData?.tripInfo?.trip_status)}
										{isInvalidTrip && (
											<Text
												strong
												style={{
													marginLeft: '6px',
													padding: '2px 8px',
													borderRadius: '4px',
													backgroundColor: '#fff1f0',
													color: '#ff4d4f',
													fontSize: '12px',
												}}
											>
												Invalid Trip
											</Text>
										)}
									</InfoValue>
								</GridItem>
							</Col>
						</Row>
					</RowWrapper>

					{tripData?.tripInfo?.trip_type === 'adhoc' && (
						<RowWrapper>
							<Row gutter={[10, 0]} style={{ alignItems: 'stretch' }}>
								<Col span={8} style={{ display: 'flex' }}>
									<GridItem>
										<InfoLabel>Remarks</InfoLabel>
										<InfoValue>{tripData?.remarks || 'N/A'}</InfoValue>
									</GridItem>
								</Col>
							</Row>
						</RowWrapper>
					)}
				</div>
			</Modal>

			<TripHistoryModal
				visible={isHistoryModalOpen}
				onCancel={() => setIsHistoryModalOpen(false)}
				historyData={historyData}
			/>
		</Card>
	);
}

TripDetailsCard.propTypes = {
	tripData: PropTypes.object,
	isValid: PropTypes.bool,
};

export default TripDetailsCard;
